'use client';

import { useState } from 'react';
import { Button } from '@core/ui/primitives/button';
import { Spinner } from '@core/ui/primitives/spinner';
import { useToast } from '@/core/ui/hooks/use-toast';
import { LuArchiveRestore, LuTrash2 } from 'react-icons/lu';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@core/ui/primitives/alert-dialog';
import { Input } from '@core/ui/primitives/input';

interface VaultRecoveryPanelProps {
  vaultId: string;
  vaultTitle: string;
  onRecovered?: () => void | Promise<void>;
  onPurged?: () => void | Promise<void>;
  canRecover?: boolean;
  canPurge?: boolean;
}

export function VaultRecoveryPanel({
  vaultId,
  vaultTitle,
  onRecovered,
  onPurged,
  canRecover = false,
  canPurge = false,
}: VaultRecoveryPanelProps) {
  const [isRecovering, setIsRecovering] = useState(false);
  const [isPurging, setIsPurging] = useState(false);
  const [showPurgeDialog, setShowPurgeDialog] = useState(false);
  const [purgeVaultName, setPurgeVaultName] = useState('');
  const { toast } = useToast();

  const handleRecover = async () => {
    setIsRecovering(true);
    try {
      const response = await fetch(`/api/vaults/${vaultId}/recover`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        if (data.code === 'SLUG_CONFLICT') {
          throw new Error('Another vault with the same slug already exists. Please rename or delete it first.');
        }
        throw new Error(data.error || 'Failed to recover vault');
      }

      toast({
        title: 'Vault recovered',
        description: 'The vault has been restored and is now accessible.',
        variant: 'success',
      });

      await onRecovered?.();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsRecovering(false);
    }
  };

  const handlePurge = async () => {
    setIsPurging(true);
    try {
      const response = await fetch(`/api/vaults/${vaultId}/purge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vaultName: purgeVaultName }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to permanently delete vault');
      }

      toast({
        title: 'Vault permanently deleted',
        description: 'The vault and all its data have been permanently removed.',
        variant: 'success',
      });

      setShowPurgeDialog(false);
      await onPurged?.();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsPurging(false);
    }
  };

  return (
    <div className='flex h-full flex-col items-center justify-center gap-6 p-8'>
      <div className='flex flex-col items-center gap-4 text-center max-w-md'>
        <div className='flex h-16 w-16 items-center justify-center rounded-full bg-muted'>
          <LuTrash2 className='h-8 w-8 text-muted-foreground' />
        </div>

        <div className='space-y-2'>
          <h2 className='text-xl font-semibold'>This vault is in the trash</h2>
          <p className='text-sm text-muted-foreground'>
            The vault <strong>{vaultTitle}</strong> has been deleted and is pending permanent removal.
            You can recover it to restore access, or permanently delete it now.
          </p>
        </div>

        {(canRecover || canPurge) ? (
          <div className='flex gap-3 pt-4'>
            {canRecover && (
              <Button
                onClick={handleRecover}
                disabled={isRecovering}
                className='gap-2'>
                {isRecovering ? (
                  <Spinner className='h-4 w-4' />
                ) : (
                  <LuArchiveRestore className='h-4 w-4' />
                )}
                Recover Vault
              </Button>
            )}

            {canPurge && (
              <Button
                variant='outline'
                onClick={() => setShowPurgeDialog(true)}
                disabled={isPurging}
                className='gap-2 text-destructive hover:text-destructive'>
                <LuTrash2 className='h-4 w-4' />
                Delete Permanently
              </Button>
            )}
          </div>
        ) : (
          <p className='text-xs text-muted-foreground pt-4'>
            You don't have permission to recover or delete this vault. Contact an administrator.
          </p>
        )}
      </div>

      <AlertDialog open={showPurgeDialog} onOpenChange={setShowPurgeDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently Delete Vault</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className='space-y-3'>
                <p className='text-destructive font-medium'>
                  This will <strong>permanently delete</strong> the vault and all its data. This action is
                  irreversible.
                </p>
                <p className='text-xs'>
                  All files, drafts, comments, access permissions, and settings will be permanently removed.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className='py-4 space-y-2'>
            <p className='text-sm text-muted-foreground'>
              Please type <span className='font-bold select-none'>{vaultTitle}</span> to confirm.
            </p>
            <Input
              value={purgeVaultName}
              onChange={(e) => setPurgeVaultName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && purgeVaultName === vaultTitle && !isPurging) {
                  e.preventDefault();
                  handlePurge();
                }
              }}
              placeholder='Enter vault name'
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPurging}>Cancel</AlertDialogCancel>
            <Button
              variant='destructive'
              disabled={purgeVaultName !== vaultTitle || isPurging}
              onClick={handlePurge}>
              {isPurging ? <Spinner className='mr-2 h-4 w-4' /> : null}
              Delete Permanently
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
